import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Checkbox } from "../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Search, ArrowLeft, Trash2, Truck, Plus, Calendar, BellRing, UserSearch } from "lucide-react";
import PickupDetail from "./PickupDetail";
import PickupDeliveryLookupDialog from "../PickupDeliveryLookupDialog";

export const PICKUP_STATUSES = [
  "nieuw", "ingepland", "bevestigd", "onderweg_pickup",
  "opgehaald", "onderweg_delivery", "afgeleverd",
  "geannuleerd", "no_show", "gefactureerd", "betaald",
];
export const PICKUP_STATUS_LABELS = {
  nieuw: "Nieuw", ingepland: "Ingepland", bevestigd: "Bevestigd",
  onderweg_pickup: "Onderweg naar pickup", opgehaald: "Opgehaald",
  onderweg_delivery: "Onderweg naar delivery", afgeleverd: "Afgeleverd",
  geannuleerd: "Geannuleerd", no_show: "No-show",
  gefactureerd: "Gefactureerd", betaald: "Betaald",
};
const STATUS_COLORS = {
  nieuw: "bg-blue-100 text-blue-700",
  ingepland: "bg-purple-100 text-purple-700",
  bevestigd: "bg-cyan-100 text-cyan-700",
  onderweg_pickup: "bg-yellow-100 text-yellow-800",
  opgehaald: "bg-orange-100 text-orange-700",
  onderweg_delivery: "bg-yellow-100 text-yellow-800",
  afgeleverd: "bg-green-100 text-green-700",
  geannuleerd: "bg-gray-200 text-gray-700",
  no_show: "bg-red-100 text-red-700",
  gefactureerd: "bg-emerald-100 text-emerald-800",
  betaald: "bg-emerald-200 text-emerald-900",
};

export function PickupStatusBadge({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold ${STATUS_COLORS[status] || "bg-gray-100 text-gray-700"}`}>
      {PICKUP_STATUS_LABELS[status] || status}
    </span>
  );
}

export default function PickupSection() {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [view, setView] = useState("list");  // list | day | week | month
  const [selected, setSelected] = useState(
    // Deeplink vanuit o.a. een telefoonregistratie: ?pickup_id=<id>
    () => new URLSearchParams(window.location.search).get("pickup_id") || null,
  );
  // Batch 3 · Fase 6 — Menselijke lookup dialog toggle.
  const [showLookup, setShowLookup] = useState(false);
  const canCreate = hasPermission("pickup:create");
  const canDelete = isAdminOrOwner();

  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);

  const buildDateRange = useCallback(() => {
    if (view === "day") return { date_from: todayIso, date_to: todayIso };
    if (view === "week") {
      const d = new Date(today);
      d.setDate(d.getDate() - d.getDay() + 1);
      const start = d.toISOString().slice(0, 10);
      d.setDate(d.getDate() + 6);
      return { date_from: start, date_to: d.toISOString().slice(0, 10) };
    }
    if (view === "month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);
      return { date_from: start, date_to: end };
    }
    return {};
  }, [view, todayIso]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...buildDateRange() };
      if (q) params.q = q;
      if (statusF !== "all") params.status = statusF;
      const r = await adminApi.get("/admin/pickup", { params });
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, statusF, buildDateRange]);

  useEffect(() => { load(); }, [load]);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ customer_name: "", auto_reminder: true });

  const onCreate = async () => {
    if (!createForm.customer_name.trim()) {
      toast.error("Klantnaam is verplicht");
      return;
    }
    try {
      // Jun 2026 hotfix — GEEN hardcoded pickup_country meer. Dat zette een
      // ophaalland zonder ophaaladres, waardoor een delivery-afspraak op de
      // documenten als halflege pickup verscheen ("Ophaaladres: Netherlands").
      const r = await adminApi.post("/admin/pickup", {
        customer_name: createForm.customer_name.trim(),
        auto_reminder: createForm.auto_reminder,
      });
      toast.success(createForm.auto_reminder ? "Afspraak + auto-reminder aangemaakt" : "Afspraak aangemaakt");
      setShowCreate(false);
      setCreateForm({ customer_name: "", auto_reminder: true });
      setSelected(r.data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Pickup-afspraak verwijderen?")) return;
    try {
      await adminApi.delete(`/admin/pickup/${id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (selected) {
    return (
      <div data-testid="admin-section-pickup-detail">
        <button onClick={() => { setSelected(null); load(); }} className="inline-flex items-center text-sm text-[#0055FF] hover:underline mb-4" data-testid="pickup-back-button">
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug naar pickuplijst
        </button>
        <PickupDetail pickupId={selected} onChanged={load} />
      </div>
    );
  }

  return (
    <div data-testid="admin-section-pickup">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-chivo font-black text-3xl text-[#0A192F] tracking-tight">Pickup & Delivery</h1>
          <p className="text-sm text-gray-600 mt-1">Plan, volg en factureer ophaal- en bezorgafspraken</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" className="rounded-none" data-testid="pickup-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Vernieuwen
          </Button>
          {/* Batch 3 · Fase 6 — Menselijke lookup dialog */}
          <Button
            onClick={() => setShowLookup(true)}
            variant="outline"
            size="sm"
            className="rounded-none"
            data-testid="pickup-lookup-btn"
          >
            <UserSearch className="w-4 h-4 mr-1" /> Zoek afspraak
          </Button>
          {canCreate && (
            <Button onClick={() => setShowCreate(true)} data-testid="pickup-new-button" className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs">
              <Plus className="w-4 h-4 mr-1" /> Nieuwe afspraak
            </Button>
          )}
        </div>
      </div>

      {/* Batch 3 · Fase 6 — Lookup dialog opent afspraak-detail bij selectie */}
      <PickupDeliveryLookupDialog
        open={showLookup}
        onOpenChange={setShowLookup}
        onSelect={(hit) => { if (hit?.id) setSelected(hit.id); }}
      />

      {/* View tabs */}
      <div className="mt-4 flex flex-wrap gap-1 bg-white border border-gray-200 p-1 w-fit" data-testid="pickup-view-tabs">
        {[
          ["list", "Lijst"], ["day", "Vandaag"], ["week", "Deze week"], ["month", "Deze maand"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setView(k)}
            data-testid={`pickup-view-${k}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-wide font-chivo font-bold ${view === k ? "bg-[#0A192F] text-white" : "text-gray-600 hover:bg-gray-100"}`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 bg-white border border-gray-200 p-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input data-testid="pickup-search" placeholder="Zoek op klant, stad..." value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="pickup-filter-status" className="rounded-none w-52"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {PICKUP_STATUSES.map((s) => <SelectItem key={s} value={s}>{PICKUP_STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-gray-500">Laden...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Geen afspraken in deze weergave.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] border-b border-gray-200 text-left">
              <tr>
                <th className="p-3 text-xs uppercase tracking-wide font-bold">Pickup datum</th>
                <th className="p-3 text-xs uppercase tracking-wide font-bold">Klant</th>
                <th className="p-3 text-xs uppercase tracking-wide font-bold">Stad / Land</th>
                <th className="p-3 text-xs uppercase tracking-wide font-bold">Toegewezen</th>
                <th className="p-3 text-xs uppercase tracking-wide font-bold">Status</th>
                <th className="p-3 text-xs uppercase tracking-wide font-bold">Totaal</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`pickup-row-${p.id}`} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setSelected(p.id)}>
                  <td className="p-3 text-xs whitespace-nowrap">
                    <div className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-gray-400" />{p.pickup_date || "—"}</div>
                    {p.pickup_time && <div className="text-[10px] text-gray-500 ml-4">{p.pickup_time}</div>}
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-[#0A192F]">{p.customer_name || "—"}</div>
                    {p.company_name && <div className="text-xs text-gray-500">{p.company_name}</div>}
                  </td>
                  <td className="p-3 text-xs">{p.pickup_city || "—"}{p.pickup_country ? ` · ${p.pickup_country}` : ""}</td>
                  <td className="p-3 text-xs text-gray-500">{p.assigned_to ? p.assigned_to.slice(0,8) + "…" : "—"}</td>
                  <td className="p-3"><PickupStatusBadge status={p.status} /></td>
                  <td className="p-3 text-xs whitespace-nowrap">
                    {p.pricing?.total_incl_vat != null ? `€ ${Number(p.pricing.total_incl_vat).toFixed(2)}` : "—"}
                  </td>
                  <td className="p-3 text-right">
                    {canDelete && (
                      <button onClick={(e) => { e.stopPropagation(); onDelete(p.id); }} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`pickup-delete-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Operationele P0 — Nieuwe afspraak dialog met auto-reminder checkbox */}
      {showCreate && (
        <Dialog open={true} onOpenChange={(o) => { if (!o) setShowCreate(false); }}>
          <DialogContent className="rounded-xl max-w-md" data-testid="pickup-create-dialog">
            <DialogHeader>
              <DialogTitle className="font-chivo font-black text-xl text-[#082c50]">Nieuwe pickup-afspraak</DialogTitle>
              <DialogDescription>Snel aanmaken — meer details kun je daarna toevoegen in de detailpagina.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase font-bold">Klantnaam *</Label>
                <Input value={createForm.customer_name} onChange={(e) => setCreateForm({ ...createForm, customer_name: e.target.value })} className="rounded-xl mt-1" autoFocus data-testid="pickup-create-customer-name" />
              </div>
              <label className="flex items-start gap-2 cursor-pointer p-3 bg-emerald-50 border border-emerald-200">
                <Checkbox checked={createForm.auto_reminder} onCheckedChange={(v) => setCreateForm({ ...createForm, auto_reminder: Boolean(v) })} data-testid="pickup-create-auto-reminder" className="mt-0.5" />
                <span className="text-sm">
                  <strong className="text-emerald-900 flex items-center gap-1"><BellRing className="w-3.5 h-3.5" /> Automatische reminder aanmaken</strong>
                  <span className="block text-[11px] text-emerald-800 mt-0.5">Standaard 1 dag vóór de pickup om 08:00 (of pickup-tijd). Kun je later wijzigen of verwijderen.</span>
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 mt-3">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-xl text-xs">Annuleren</Button>
              <Button onClick={onCreate} className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-xl text-xs font-chivo font-bold uppercase" data-testid="pickup-create-save">Aanmaken</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
